import { setSelectedSuggestion } from '@/hooks/llm/editor-suggestion-config';

import type { Editor } from '@tiptap/react';

type SuggestionLike = {
  id: string;
  range?: { from: number; to: number };
};

export function trySetEditorSelection(editor: Editor | null, selection: { from: number; to: number } | number): boolean {
  if (!editor) return false;

  const maxPos = editor.state.doc.content.size;
  const clampPos = (pos: number) => Math.max(0, Math.min(pos, maxPos));
  const isInlinePos = (pos: number): boolean => {
    try {
      return editor.state.doc.resolve(clampPos(pos)).parent.inlineContent;
    } catch {
      return false;
    }
  };
  const trySetNodeSelection = (pos: number): boolean => {
    try {
      editor.chain().focus().setNodeSelection(pos).run();
      return true;
    } catch {
      return false;
    }
  };

  const getNodeSelectionPos = (): number | null => {
    if (typeof selection === 'number') {
      const pos = clampPos(selection);
      if (editor.state.doc.nodeAt(pos)) return pos;
      if (pos > 0 && editor.state.doc.nodeAt(pos - 1)) return pos - 1;
      if (pos < maxPos && editor.state.doc.nodeAt(pos + 1)) return pos + 1;
      return null;
    }

    const from = clampPos(selection.from);
    const to = clampPos(selection.to);

    if (editor.state.doc.nodeAt(from)) return from;
    if (editor.state.doc.nodeAt(to)) return to;
    if (from > 0 && editor.state.doc.nodeAt(from - 1)) return from - 1;
    if (to > 0 && editor.state.doc.nodeAt(to - 1)) return to - 1;

    return null;
  };

  const getSafeTextSelection = (): { from: number; to: number } | number | null => {
    if (typeof selection === 'number') {
      const pos = clampPos(selection);
      if (isInlinePos(pos)) return pos;

      const nearbyCandidates = [pos - 1, pos + 1];
      for (const candidate of nearbyCandidates) {
        if (candidate >= 0 && candidate <= maxPos && isInlinePos(candidate)) {
          return candidate;
        }
      }

      return null;
    }

    const from = clampPos(selection.from);
    const to = clampPos(selection.to);
    if (isInlinePos(from) && isInlinePos(to)) {
      return { from, to };
    }

    return null;
  };

  const safeTextSelection = getSafeTextSelection();
  if (safeTextSelection == null) {
    const nodeSelectionPos = getNodeSelectionPos();
    return nodeSelectionPos != null ? trySetNodeSelection(nodeSelectionPos) : false;
  }

  try {
    editor.chain().focus().setTextSelection(safeTextSelection).run();
    return true;
  } catch {
    const nodeSelectionPos = getNodeSelectionPos();
    return nodeSelectionPos != null ? trySetNodeSelection(nodeSelectionPos) : false;
  }
}

export function navigateEditorToSuggestion<TSuggestion extends SuggestionLike>({
  editor,
  suggestion,
  index,
  setCurrentIndex,
}: {
  editor: Editor | null;
  suggestion?: TSuggestion;
  index: number;
  setCurrentIndex: (index: number) => void;
}): void {
  if (!editor || !suggestion?.range) return;

  const { from, to } = suggestion.range;
  trySetEditorSelection(editor, { from, to });
  setSelectedSuggestion(editor, suggestion.id);

  try {
    const editorElement = editor.view.dom;
    const previewWidgets = editorElement.querySelectorAll('.tiptap-ai-suggestion-diff');

    let targetWidget: Element | null = null;
    let closestDistance = Infinity;

    for (const widget of Array.from(previewWidgets)) {
      const isBlockLevelNode = widget.parentElement === editorElement;

      if (isBlockLevelNode) {
        const widgetRect = widget.getBoundingClientRect();
        const selection = window.getSelection();

        if (selection && selection.rangeCount > 0) {
          const selectionRect = selection.getRangeAt(0).getBoundingClientRect();
          const distance = Math.abs(widgetRect.top - selectionRect.top);

          if (distance < closestDistance) {
            closestDistance = distance;
            targetWidget = widget;
          }
        } else if (!targetWidget) {
          targetWidget = widget;
        }
      }
    }

    if (targetWidget) {
      targetWidget.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    } else {
      scrollSelectionIntoViewSmoothCenter(editor);
    }
  } catch {
    scrollSelectionIntoViewSmoothCenter(editor);
  }

  setCurrentIndex(index);
}

function scrollSelectionIntoViewSmoothCenter(editor: Editor): void {
  if (!editor?.view) return;

  requestAnimationFrame(() => {
    const { view, state } = editor;
    const domAt = view.domAtPos(state.selection.from);
    let el: HTMLElement | null = domAt?.node
      ? domAt.node.nodeType === Node.TEXT_NODE
        ? (domAt.node as Text).parentElement
        : (domAt.node as HTMLElement)
      : null;

    if (!el) el = view.dom as HTMLElement;

    const block = el.closest<HTMLElement>('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, figure') || el;

    block.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  });
}
